(function () {
  const SELECTOR = '[data-free-product-selector]';

  function parseConfig(root) {
    const configNode = root.querySelector('[data-free-product-config]');
    if (!configNode) return {};

    try {
      return JSON.parse(configNode.textContent);
    } catch (error) {
      console.warn('[FreeProductSelector] Invalid config', error);
      return {};
    }
  }

  function hasMarketingConsent() {
    try {
      return !window.Shopify ||
        !window.Shopify.customerPrivacy ||
        window.Shopify.customerPrivacy.marketingAllowed();
    } catch (error) {
      return true;
    }
  }

  async function sha256hex(value) {
    if (!window.crypto || !window.crypto.subtle || !window.TextEncoder) return null;

    const data = new TextEncoder().encode(value);
    const hash = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hash))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  function formatEuro(valueCents) {
    const amount = Number(valueCents || 0) / 100;
    return `€${amount.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  function getOfferValueCents(card) {
    return Number(card?.dataset?.offerValueCents || 0);
  }

  function localDateString(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  function getCookie(name) {
    const needle = `${name}=`;
    return document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.indexOf(needle) === 0)
      ?.slice(needle.length) || '';
  }

  function setCookie(name, value) {
    const expires = new Date();
    expires.setHours(24, 0, 0, 0);
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  function countFromTime(date) {
    const seed = date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
    const baseCount = 47 + (seed % 7);
    const minutes = (date.getHours() * 60) + date.getMinutes();
    let reduction = 0;

    if (minutes < 480) {
      reduction = Math.floor(minutes / 240);
    } else if (minutes < 600) {
      reduction = 2 + Math.floor((minutes - 480) / 40);
    } else if (minutes < 720) {
      reduction = 5 + Math.floor((minutes - 600) / 20);
    } else if (minutes < 840) {
      reduction = 11 + Math.floor((minutes - 720) / 17);
    } else if (minutes < 1020) {
      reduction = 18 + Math.floor((minutes - 840) / 15);
    } else if (minutes < 1140) {
      reduction = 30 + Math.floor((minutes - 1020) / 15);
    } else if (minutes < 1320) {
      reduction = 38 + Math.floor((minutes - 1140) / 45);
    } else {
      reduction = 42 + Math.floor((minutes - 1320) / 40);
    }

    reduction = Math.max(0, reduction + ((date.getMinutes() % 3) - 1));
    return Math.max(3, baseCount - reduction);
  }

  function fireMetaEvents(email, offer, offerValueCents) {
    if (typeof window.fbq !== 'function' || !hasMarketingConsent()) return;

    const leadEventId = `lead_lp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    const regEventId = `reg_lp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    const normalizedEmail = email.toLowerCase().trim();

    const eventValue = Math.max(0, Number(offerValueCents || 0) / 100);

    const track = (emailHash) => {
      if (emailHash && window.fbq.instance && window.fbq.instance.pixelsByID) {
        Object.keys(window.fbq.instance.pixelsByID).forEach((pixelId) => {
          window.fbq('init', pixelId, { em: emailHash });
        });
      }

      window.fbq('track', 'Lead', {
        content_name: 'Landing Page Free Product Signup',
        content_category: 'Newsletter',
        lead_source: 'landing-freies-produkt',
        selected_offer: offer,
        value: eventValue || 20.00,
        currency: 'EUR'
      }, { eventID: leadEventId });

      window.fbq('track', 'CompleteRegistration', {
        content_name: 'Newsletter + Free Product',
        status: true,
        value: eventValue || 20.00,
        currency: 'EUR'
      }, { eventID: regEventId });
    };

    sha256hex(normalizedEmail).then(track).catch(() => track(null));
  }

  class FreeProductSelector {
    constructor(root) {
      this.root = root;
      this.config = parseConfig(root);
      this.cards = Array.from(root.querySelectorAll('[data-free-product-card]'))
        .filter((card) => !card.disabled && card.getAttribute('aria-disabled') !== 'true' && !card.classList.contains('is-sold-out'));
      this.form = root.querySelector('[data-free-product-form]');
      this.email = root.querySelector('[data-free-product-email]');
      this.stickyEmail = root.querySelector('[data-free-product-sticky-email]');
      this.stickyButton = root.querySelector('[data-free-product-sticky-submit]');
      this.sticky = root.querySelector('[data-free-product-sticky]');
      this.submitButton = root.querySelector('[data-free-product-submit]');
      this.selectedInput = root.querySelector('[data-free-product-selected-offer], [data-free-product-selected]');
      this.maxValue = root.querySelector('[data-free-product-max-value]');
      this.maxValueNote = root.querySelector('[data-free-product-max-note]');
      this.remaining = root.querySelector('[data-free-product-remaining]');
      this.urgency = root.querySelector('[data-free-product-urgency]');
      this.error = root.querySelector('[data-free-product-error]');
      this.success = root.querySelector('[data-free-product-success]');
      this.selectedCard = null;

      this.initUrgencyCounter();
      this.updateMaxValueStamp();
      this.bindCards();
      this.bindForm();
      this.bindSticky();
      this.selectInitialCard();
    }

    updateMaxValueStamp() {
      if (!this.maxValue || !this.cards.length) return;

      const maxValueCents = this.cards.reduce((highest, card) => {
        return Math.max(highest, getOfferValueCents(card));
      }, 0);

      if (maxValueCents > 0) this.maxValue.textContent = formatEuro(maxValueCents);

      if (this.maxValueNote) {
        this.maxValueNote.textContent = `Spar dir bis zu ${formatEuro(maxValueCents)} beim ersten Einkauf.`;
      }
    }

    initUrgencyCounter() {
      if (!this.remaining) return;

      const countCookie = 'metzger_urgency_count';
      const dateCookie = 'metzger_urgency_date';
      const today = localDateString(new Date());
      const cookieDate = getCookie(dateCookie);
      let remaining = Number(getCookie(countCookie));

      if (!remaining || cookieDate !== today) {
        remaining = countFromTime(new Date());
        setCookie(countCookie, String(remaining));
        setCookie(dateCookie, today);
      }

      const update = () => {
        this.remaining.textContent = remaining;
      };

      const flash = () => {
        if (!this.urgency) return;
        this.urgency.classList.add('is-flashing');
        window.setTimeout(() => this.urgency.classList.remove('is-flashing'), 650);
      };

      const decrement = () => {
        if (remaining <= 3) return;
        remaining -= 1;
        setCookie(countCookie, String(remaining));
        update();
        flash();
      };

      update();
      window.setTimeout(decrement, 20000 + Math.random() * 10000);
      window.setInterval(decrement, 45000 + Math.random() * 30000);
    }

    selectInitialCard() {
      if (!this.cards.length || this.config.autoSelectFirst === false) return;

      const activeCard = this.cards.find((card) => card.classList.contains('is-selected') || card.getAttribute('aria-pressed') === 'true');
      this.selectCard(activeCard || this.cards[0], { silent: true });
    }

    bindCards() {
      this.cards.forEach((card) => {
        card.addEventListener('click', () => this.selectCard(card));
        card.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.selectCard(card);
          }
        });
      });
    }

    bindForm() {
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => this.submit(event));
    }

    bindSticky() {
      if (!this.sticky) return;

      if (this.stickyEmail && this.email) {
        this.stickyEmail.addEventListener('input', () => {
          this.email.value = this.stickyEmail.value;
        });
        this.email.addEventListener('input', () => {
          this.stickyEmail.value = this.email.value;
        });
      }

      if (this.stickyButton && this.form) {
        this.stickyButton.addEventListener('click', () => {
          if (this.stickyEmail && this.email) this.email.value = this.stickyEmail.value;
          this.form.requestSubmit();
        });
      }

      const updateSticky = () => {
        const rootTop = this.root.getBoundingClientRect().top + window.scrollY;
        const rootBottom = rootTop + this.root.offsetHeight;
        const showAfterSelectorIntro = window.scrollY > rootTop + 900;
        const stillInsideSelector = window.scrollY < rootBottom - 220;
        const isMobile = window.matchMedia('(max-width: 989px)').matches;
        this.sticky.classList.toggle('is-visible', isMobile && showAfterSelectorIntro && stillInsideSelector && !this.root.classList.contains('is-complete'));
      };

      updateSticky();
      window.addEventListener('scroll', updateSticky, { passive: true });
      window.addEventListener('resize', updateSticky, { passive: true });
    }

    selectCard(card, options = {}) {
      if (!card || card.disabled || card.classList.contains('is-sold-out')) return;

      this.cards.forEach((node) => {
        node.classList.remove('is-selected');
        node.setAttribute('aria-pressed', 'false');
      });

      card.classList.add('is-selected');
      card.setAttribute('aria-pressed', 'true');
      this.selectedCard = card;

      if (this.selectedInput) this.selectedInput.value = card.dataset.offerValue || '';
      if (this.submitButton) {
        this.submitButton.disabled = false;
        this.submitButton.textContent = `Gratis ${card.dataset.offerTitle || 'Produkt'} sichern`;
      }
      if (!options.silent) this.setError('');
    }

    setError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    async submit(event) {
      event.preventDefault();

      if (!this.selectedCard) {
        this.setError('Bitte wähle zuerst dein Gratis-Produkt aus.');
        const grid = this.root.querySelector('[data-free-product-grid]');
        if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      if (!this.email || !this.email.checkValidity()) {
        this.email.reportValidity();
        return;
      }

      const email = this.email.value.trim();
      const selectedOffer = this.selectedCard.dataset.offerValue || '';
      const selectedOfferValueCents = getOfferValueCents(this.selectedCard);
      const variantId = this.selectedCard.dataset.variantId || '';
      const isFallback = this.selectedCard.dataset.fallback === 'true';

      this.setLoading(true);

      try {
        await this.subscribe(email, selectedOffer, isFallback, selectedOfferValueCents);
        await this.addGiftToCart(variantId, selectedOffer, isFallback, selectedOfferValueCents);
        this.track(email, selectedOffer, selectedOfferValueCents);
        this.showSuccess();
      } catch (error) {
        console.error('[FreeProductSelector] Submit failed', error);
        this.setError('Das hat nicht geklappt. Bitte prüfe deine E-Mail und versuche es erneut.');
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isLoading;
      this.submitButton.textContent = isLoading ? 'Wird gesichert...' : `Gratis ${this.selectedCard?.dataset.offerTitle || 'Produkt'} sichern`;
      if (this.stickyButton) this.stickyButton.disabled = isLoading;
    }

    async subscribe(email, selectedOffer, isFallback, selectedOfferValueCents) {
      if (!this.config.klaviyoListId) return;

      const params = new URLSearchParams();
      params.append('g', this.config.klaviyoListId);
      params.append('email', email);
      params.append('SelectedOffer', selectedOffer);
      params.append('FallbackSelected', isFallback ? 'true' : 'false');
      params.append('SoldOutProducts', this.config.soldOutProducts || '');
      params.append('OfferValueCents', String(selectedOfferValueCents || 0));
      params.append('$source', this.config.source || 'landing-freies-produkt');
      params.append('$fields', 'SelectedOffer,FallbackSelected,SoldOutProducts,OfferValueCents,$source');
      params.append('$consent', 'email');

      window._learnq = window._learnq || [];
      window._learnq.push(['identify', {
        '$email': email,
        'SelectedOffer': selectedOffer,
        'FallbackSelected': isFallback ? 'true' : 'false',
        'SoldOutProducts': this.config.soldOutProducts || '',
        'OfferValueCents': selectedOfferValueCents || 0
      }]);

      const response = await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
        method: 'POST',
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
          'cache-control': 'no-cache'
        },
        body: params
      });

      const data = await response.json();
      if (!data.success) throw new Error('Klaviyo subscription failed');
    }

    async addGiftToCart(variantId, selectedOffer, isFallback, selectedOfferValueCents) {
      if (!variantId) return;

      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: Number(variantId),
            quantity: 1,
            properties: {
              _free_product_landing: 'landing-freies-produkt',
              _selected_offer: selectedOffer,
              _fallback_selected: isFallback ? 'true' : 'false',
              _offer_value_cents: String(selectedOfferValueCents || 0)
            }
          }]
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || `Cart add failed with ${response.status}`);
      }
    }

    track(email, selectedOffer, selectedOfferValueCents) {
      if (window._learnq) {
        window._learnq.push(['track', 'Subscribed to List', {
          'ListID': this.config.klaviyoListId,
          'Offer': selectedOffer,
          'OfferValueCents': selectedOfferValueCents || 0,
          '$source': this.config.source || 'landing-freies-produkt'
        }]);
      }

      if (typeof window.fbq === 'function') {
        fireMetaEvents(email, selectedOffer, selectedOfferValueCents);
      } else {
        window.addEventListener('essentialScriptsLoaded', () => fireMetaEvents(email, selectedOffer, selectedOfferValueCents), { once: true });
      }
    }

    showSuccess() {
      if (this.success) this.success.hidden = false;
      if (this.form) this.form.classList.add('is-complete');
      this.root.classList.add('is-complete');

      const delay = Number(this.config.redirectDelay || 2500);
      window.setTimeout(() => {
        window.location.href = this.config.redirectUrl || '/collections/bio-fleisch-bestseller';
      }, delay);
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(SELECTOR).forEach((root) => new FreeProductSelector(root));
  });
})();
